import { cn, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@myosh/odin-components';
import { type Column, flexRender, getCoreRowModel, useReactTable, type VisibilityState } from '@tanstack/react-table';
import type { CSSProperties } from 'react';
import type { buildScheduleColumns } from '@/components/schedules/columns';
import type { ScheduleRow } from '@/utils/schedule-records';

interface SchedulesTableProps {
  label: string;
  columns: ReturnType<typeof buildScheduleColumns>;
  rows: ScheduleRow[];
  columnVisibility: VisibilityState;
  pinnedColumnIds: string[];
  isFetching: boolean;
}

const columnStyle = (column: Column<ScheduleRow>): CSSProperties => ({
  width: column.getSize(),
  minWidth: column.getSize(),
  left: column.getIsPinned() === 'left' ? column.getStart('left') : undefined,
});

const pinnedClassName = (column: Column<ScheduleRow>) =>
  column.getIsPinned() === 'left' && cn('sticky z-10 bg-background', column.getIsLastColumn('left') && 'shadow-right');

// Pinned columns stay put while the period columns scroll sideways under them.
export const SchedulesTable = ({
  label,
  columns,
  rows,
  columnVisibility,
  pinnedColumnIds,
  isFetching,
}: SchedulesTableProps) => {
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    state: { columnVisibility, columnPinning: { left: pinnedColumnIds } },
  });

  return (
    <Table
      className={cn('table-fixed', isFetching && 'opacity-60')}
      style={{ width: table.getTotalSize() }}
      aria-label={label}
      aria-busy={isFetching}
    >
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                style={columnStyle(header.column)}
                className={cn('truncate', pinnedClassName(header.column))}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                style={columnStyle(cell.column)}
                className={cn('truncate', pinnedClassName(cell.column))}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
